import {
  IconArrowBackUp,
  IconArrowForwardUp,
  IconBlockquote,
  IconBold,
  IconClearFormatting,
  IconItalic,
  IconLink,
  IconList,
  IconListNumbers,
  IconMaximize,
  IconMinimize,
  IconColumnInsertRight,
  IconColumnRemove,
  IconPhoto,
  IconPhotoMinus,
  IconPhotoPlus,
  IconRowInsertBottom,
  IconRowRemove,
  IconStrikethrough,
  IconTable,
  IconTableOff,
  IconTrash,
  IconUnderline,
} from "@tabler/icons-react";
import { buildMessageSchema } from "@chatwoot/prosemirror-schema";
import {
  baseKeymap,
  lift,
  setBlockType,
  toggleMark,
  wrapIn,
} from "prosemirror-commands";
import { history, redo, undo } from "prosemirror-history";
import { keymap } from "prosemirror-keymap";
import {
  DOMParser as ProseMirrorDOMParser,
  DOMSerializer,
  Schema,
  type MarkSpec,
  type MarkType,
  type Node as ProseMirrorNode,
  type NodeSpec,
  type NodeType,
} from "prosemirror-model";
import { liftListItem, wrapInList } from "prosemirror-schema-list";
import {
  EditorState,
  NodeSelection,
  TextSelection,
  type Command,
} from "prosemirror-state";
import {
  addColumnAfter,
  addRowAfter,
  deleteColumn,
  deleteRow,
  deleteTable,
  tableEditing,
} from "prosemirror-tables";
import { EditorView } from "prosemirror-view";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useId,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { CaseMentionUser } from "./api";

export type RichTextSnapshot = {
  html: string;
  text: string;
};

export type CaseRichTextEditorHandle = {
  clear: () => void;
  focus: () => void;
  getSnapshot: () => RichTextSnapshot;
  setHtml: (html: string) => void;
};

type Props = {
  ariaLabel: string;
  disabled?: boolean;
  onChange?: (snapshot: RichTextSnapshot) => void;
  onUploadImage?: (file: File) => Promise<{ src: string; filename: string }>;
  onUploadStateChange?: (uploading: boolean) => void;
  placeholder?: string;
  initialHtml?: string;
  resetKey?: string | number;
  mentionUsers?: CaseMentionUser[];
  mentionStatus?: string;
  onRetryMentions?: () => void;
  onMentionsOpen?: () => void;
};

const schema = emailSchema();

export const CaseRichTextEditor = forwardRef<CaseRichTextEditorHandle, Props>(
  function CaseRichTextEditor(
    {
      ariaLabel,
      disabled = false,
      onChange,
      onUploadImage,
      onUploadStateChange,
      placeholder = "输入消息内容",
      initialHtml = "",
      resetKey,
      mentionUsers,
      mentionStatus,
      onRetryMentions,
      onMentionsOpen,
    },
    forwardedRef,
  ) {
    const host = useRef<HTMLDivElement>(null);
    const view = useRef<EditorView | null>(null);
    const changeHandler = useRef(onChange);
    const [revision, setRevision] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [failedUpload, setFailedUpload] = useState<{
      file: File;
      reason: string;
    } | null>(null);
    const imageInput = useRef<HTMLInputElement>(null);
    const uploadingRef = useRef(false);
    const uploadHandler = useRef(onUploadImage);
    const uploadStateHandler = useRef(onUploadStateChange);
    const lastResetKey = useRef(resetKey);
    const initialHtmlRef = useRef(initialHtml);
    const mentionListId = useId();
    const mentionOpenHandler = useRef(onMentionsOpen);
    mentionOpenHandler.current = onMentionsOpen;
    const [mentionIndex, setMentionIndex] = useState(0);
    const [dismissedMention, setDismissedMention] = useState("");
    const mentionKeyHandler = useRef<(event: KeyboardEvent) => boolean>(
      () => false,
    );
    changeHandler.current = onChange;
    initialHtmlRef.current = initialHtml;
    uploadHandler.current = onUploadImage;
    uploadStateHandler.current = onUploadStateChange;

    const uploadImage = useCallback(async (file: File) => {
      const handler = uploadHandler.current;
      if (!handler || uploadingRef.current) return;
      uploadingRef.current = true;
      setUploading(true);
      setFailedUpload(null);
      uploadStateHandler.current?.(true);
      try {
        const uploaded = await handler(file);
        const editor = view.current;
        if (!editor) return;
        const image = schema.nodes.image.create({
          src: uploaded.src,
          alt: uploaded.filename,
          title: uploaded.filename,
          width: 480,
        });
        editor.dispatch(editor.state.tr.replaceSelectionWith(image));
        editor.focus();
      } catch (error: unknown) {
        setFailedUpload({
          file,
          reason: error instanceof Error ? error.message : "图片上传失败",
        });
      } finally {
        uploadingRef.current = false;
        setUploading(false);
        uploadStateHandler.current?.(false);
        if (imageInput.current) imageInput.current.value = "";
      }
    }, []);

    const buttons = useMemo(
      () => toolbarButtons(schema, () => view.current, setRevision),
      [],
    );

    useEffect(() => {
      if (!host.current) return;
      const editor = new EditorView(host.current, {
        state: createState(schema, initialHtmlRef.current),
        editable: () => !disabled,
        attributes: {
          class: "case-rich-editor",
          "aria-label": ariaLabel,
          "aria-multiline": "true",
          "data-placeholder": placeholder,
          role: "textbox",
        },
        dispatchTransaction(transaction) {
          const next = editor.state.apply(transaction);
          editor.updateState(next);
          setRevision((value) => value + 1);
          if (transaction.docChanged) {
            changeHandler.current?.(snapshotFromDocument(next.doc));
          }
        },
        handleKeyDown(_view, event) {
          if (event.isComposing || _view.composing) return false;
          return mentionKeyHandler.current(event);
        },
        handlePaste(_editorView, event) {
          const image = [...(event.clipboardData?.files ?? [])].find((file) =>
            file.type.startsWith("image/"),
          );
          if (!image || !uploadHandler.current) return false;
          event.preventDefault();
          void uploadImage(image);
          return true;
        },
      });
      const handleNativeInput = (event: Event) => {
        // Testing-library mutates contentEditable directly. Synchronize that
        // synthetic DOM change before ProseMirror's observer tries to measure a
        // jsdom selection. Trusted browser input keeps the normal transaction
        // and history path above.
        if (!event.isTrusted) {
          const next = createState(schema, editor.dom.innerHTML);
          editor.updateState(
            next.apply(next.tr.setSelection(TextSelection.atEnd(next.doc))),
          );
          setRevision((value) => value + 1);
        }
        changeHandler.current?.(snapshotFromDocument(editor.state.doc));
      };
      editor.dom.addEventListener("input", handleNativeInput);
      view.current = editor;
      setRevision((value) => value + 1);
      return () => {
        editor.dom.removeEventListener("input", handleNativeInput);
        view.current = null;
        editor.destroy();
      };
    }, [uploadImage]);

    useEffect(() => {
      const editor = view.current;
      if (!editor) return;
      editor.setProps({ editable: () => !disabled });
      editor.dom.setAttribute("aria-disabled", String(disabled));
    }, [disabled]);

    useEffect(() => {
      const editor = view.current;
      if (!editor) return;
      editor.dom.setAttribute("data-placeholder", placeholder);
    }, [placeholder]);

    useEffect(() => {
      view.current?.dom.setAttribute("aria-label", ariaLabel);
    }, [ariaLabel]);

    useEffect(() => {
      if (!fullscreen) return;
      const onKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          event.preventDefault();
          setFullscreen(false);
        }
      };
      document.addEventListener("keydown", onKeyDown);
      return () => document.removeEventListener("keydown", onKeyDown);
    }, [fullscreen]);

    useEffect(() => {
      if (lastResetKey.current === resetKey) return;
      lastResetKey.current = resetKey;
      const snapshot = replaceDocument(view.current, initialHtmlRef.current);
      if (snapshot) changeHandler.current?.(snapshot);
      setRevision((value) => value + 1);
    }, [resetKey]);

    useImperativeHandle(
      forwardedRef,
      () => ({
        clear: () => {
          const snapshot = replaceDocument(view.current, "");
          if (snapshot) changeHandler.current?.(snapshot);
          setRevision((value) => value + 1);
        },
        focus: () => view.current?.focus(),
        getSnapshot: () =>
          view.current
            ? snapshotFromHtml(view.current.dom.innerHTML)
            : { html: "", text: "" },
        setHtml: (html) => {
          const snapshot = replaceDocument(view.current, html);
          if (snapshot) changeHandler.current?.(snapshot);
          setRevision((value) => value + 1);
        },
      }),
      [],
    );

    const mention =
      !disabled && mentionUsers !== undefined && view.current
        ? mentionAtCursor(view.current.state)
        : null;
    const mentionKey = mention
      ? `${mention.from}:${mention.to}:${mention.query}`
      : "";
    const mentionOpen = Boolean(mention && mentionKey !== dismissedMention);
    useEffect(() => {
      if (mentionOpen) mentionOpenHandler.current?.();
    }, [mentionOpen]);
    const matches = (mentionUsers ?? []).filter((user) =>
      `${user.displayName} ${user.username ?? ""}`
        .toLocaleLowerCase()
        .includes((mention?.query ?? "").toLocaleLowerCase()),
    );
    const activeMention = Math.min(
      mentionIndex,
      Math.max(0, matches.length - 1),
    );
    useEffect(() => {
      setMentionIndex(0);
      if (!mentionKey) setDismissedMention("");
    }, [mentionKey]);
    useEffect(() => {
      const dom = view.current?.dom;
      if (mentionOpen) {
        dom?.setAttribute("aria-controls", mentionListId);
        dom?.setAttribute("aria-autocomplete", "list");
        if (matches.length)
          dom?.setAttribute(
            "aria-activedescendant",
            `${mentionListId}-${activeMention}`,
          );
        else dom?.removeAttribute("aria-activedescendant");
        document
          .getElementById(`${mentionListId}-${activeMention}`)
          ?.scrollIntoView?.({ block: "nearest" });
      } else {
        dom?.removeAttribute("aria-controls");
        dom?.removeAttribute("aria-autocomplete");
        dom?.removeAttribute("aria-activedescendant");
      }
    }, [mentionOpen, activeMention, matches.length, mentionListId]);
    const insertMention = (user: CaseMentionUser) => {
      const editor = view.current;
      if (!editor || !mention || disabled) return;
      const node = schema.nodes.mention.create({
        userId: user.id,
        label: user.displayName,
      });
      const tr = editor.state.tr.replaceWith(mention.from, mention.to, [
        node,
        schema.text(" "),
      ]);
      tr.setSelection(
        TextSelection.create(tr.doc, mention.from + node.nodeSize + 1),
      );
      editor.dispatch(tr);
      editor.focus();
    };
    mentionKeyHandler.current = (event) => {
      if (!mentionOpen) return false;
      if (event.key === "Escape") {
        setDismissedMention(mentionKey);
        return true;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        if (matches.length)
          setMentionIndex(
            (activeMention +
              (event.key === "ArrowDown" ? 1 : -1) +
              matches.length) %
              matches.length,
          );
        return true;
      }
      if (event.key === "Enter" && matches[activeMention]) {
        insertMention(matches[activeMention]);
        return true;
      }
      return false;
    };

    return (
      <div
        className={`case-rich-editor-shell${fullscreen ? " fullscreen" : ""}`}
        data-revision={revision}
      >
        <div
          className="case-editor-toolbar"
          role="toolbar"
          aria-label="富文本工具栏"
        >
          {mentionUsers !== undefined && (
            <button
              type="button"
              aria-label="@ 用户"
              title="@ 用户"
              disabled={disabled}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                const editor = view.current;
                if (!editor) return;
                setDismissedMention("");
                if (!mention) {
                  const { $from } = editor.state.selection;
                  const before = $from.parent.textBetween(
                    0,
                    $from.parentOffset,
                    "",
                    "\ufffc",
                  );
                  editor.dispatch(
                    editor.state.tr.insertText(
                      before && !/\s$/.test(before) ? " @" : "@",
                    ),
                  );
                }
                editor.focus();
              }}
            >
              @
            </button>
          )}
          {buttons.map((button) => {
            const editor = view.current;
            const active = editor ? button.active(editor.state) : false;
            const enabled = editor ? button.enabled(editor.state) : false;
            const Icon = button.icon;
            return (
              <button
                key={button.key}
                type="button"
                className={active ? "active" : ""}
                aria-label={button.label}
                aria-pressed={button.toggle ? active : undefined}
                title={button.label}
                disabled={disabled || !enabled}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (!disabled) button.run();
                }}
              >
                <Icon size={16} aria-hidden="true" />
              </button>
            );
          })}
          {onUploadImage && (
            <>
              <button
                type="button"
                aria-label="插入图片"
                title="插入图片"
                disabled={disabled || uploading}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => imageInput.current?.click()}
              >
                <IconPhoto size={16} aria-hidden="true" />
              </button>
              <input
                ref={imageInput}
                className="case-editor-file-input"
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                disabled={disabled || uploading}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void uploadImage(file);
                }}
              />
            </>
          )}
          <span className="case-editor-toolbar-spacer" aria-hidden="true" />
          <button
            type="button"
            className={fullscreen ? "active" : ""}
            aria-label={fullscreen ? "退出全屏编辑" : "全屏编辑"}
            aria-pressed={fullscreen}
            title={fullscreen ? "退出全屏编辑（Esc）" : "全屏编辑"}
            disabled={disabled && !fullscreen}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setFullscreen((value) => !value);
              window.setTimeout(() => view.current?.focus(), 0);
            }}
          >
            {fullscreen ? (
              <IconMinimize size={16} aria-hidden="true" />
            ) : (
              <IconMaximize size={16} aria-hidden="true" />
            )}
          </button>
        </div>
        {mentionOpen && (
          <div className="case-mention-picker">
            <div className="case-mention-heading">选择要 @ 的用户</div>
            <div
              id={mentionListId}
              role="listbox"
              aria-label="@ 用户列表"
              className="case-mention-options"
            >
              {matches.map((user, index) => (
                <button
                  key={user.id}
                  id={`${mentionListId}-${index}`}
                  type="button"
                  role="option"
                  aria-label={`${user.displayName}${user.username ? ` ${user.username}` : ""}`}
                  aria-selected={index === activeMention}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => insertMention(user)}
                >
                  <strong>{user.displayName}</strong>
                  {user.username && <small>{user.username}</small>}
                </button>
              ))}
            </div>
            {!matches.length && (
              <small role="status">{mentionStatus || "没有匹配的用户"}</small>
            )}
            {mentionStatus?.includes("失败") && (
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={onRetryMentions}
              >
                重试加载用户
              </button>
            )}
          </div>
        )}
        <div ref={host} className="case-rich-editor-host" />
        {uploading && (
          <small className="case-editor-upload-status">图片上传中…</small>
        )}
        {failedUpload && (
          <div className="case-editor-upload-status error" role="alert">
            <span title={failedUpload.reason}>
              图片上传失败：{failedUpload.reason}
            </span>
            <button
              type="button"
              onClick={() => void uploadImage(failedUpload.file)}
            >
              重试
            </button>
            <button type="button" onClick={() => setFailedUpload(null)}>
              删除
            </button>
          </div>
        )}
      </div>
    );
  },
);

type ToolbarButton = {
  key: string;
  label: string;
  icon: typeof IconBold;
  toggle?: boolean;
  active: (state: EditorState) => boolean;
  enabled: (state: EditorState) => boolean;
  run: () => void;
};

function toolbarButtons(
  editorSchema: Schema,
  currentView: () => EditorView | null,
  refresh: Dispatch<SetStateAction<number>>,
): ToolbarButton[] {
  const commandButton = (
    key: string,
    label: string,
    icon: typeof IconBold,
    command: () => Command,
    active: (state: EditorState) => boolean = () => false,
    toggle = false,
  ): ToolbarButton => ({
    key,
    label,
    icon,
    toggle,
    active,
    enabled: (state) => command()(state),
    run: () => {
      const view = currentView();
      if (!view) return;
      command()(view.state, view.dispatch, view);
      view.focus();
      refresh((value) => value + 1);
    },
  });
  const markButton = (
    key: string,
    label: string,
    icon: typeof IconBold,
    mark: MarkType,
  ) =>
    commandButton(
      key,
      label,
      icon,
      () => toggleMark(mark),
      (state) => markActive(state, mark),
      true,
    );
  const listButton = (
    key: string,
    label: string,
    icon: typeof IconBold,
    list: NodeType,
  ) =>
    commandButton(
      key,
      label,
      icon,
      () => (state, dispatch) =>
        nodeActive(state, list)
          ? liftListItem(editorSchema.nodes.list_item)(state, dispatch)
          : wrapInList(list)(state, dispatch),
      (state) => nodeActive(state, list),
      true,
    );

  return [
    markButton("bold", "加粗", IconBold, editorSchema.marks.strong),
    markButton("italic", "斜体", IconItalic, editorSchema.marks.em),
    markButton(
      "underline",
      "下划线",
      IconUnderline,
      editorSchema.marks.underline,
    ),
    markButton(
      "strike",
      "删除线",
      IconStrikethrough,
      editorSchema.marks.strike,
    ),
    listButton(
      "bullet-list",
      "无序列表",
      IconList,
      editorSchema.nodes.bullet_list,
    ),
    listButton(
      "ordered-list",
      "有序列表",
      IconListNumbers,
      editorSchema.nodes.ordered_list,
    ),
    commandButton(
      "blockquote",
      "引用",
      IconBlockquote,
      () => (state, dispatch) =>
        nodeActive(state, editorSchema.nodes.blockquote)
          ? lift(state, dispatch)
          : wrapIn(editorSchema.nodes.blockquote)(state, dispatch),
      (state) => nodeActive(state, editorSchema.nodes.blockquote),
      true,
    ),
    {
      key: "link",
      label: "链接",
      icon: IconLink,
      toggle: true,
      active: (state) => markActive(state, editorSchema.marks.link),
      enabled: (state) => !state.selection.empty,
      run: () => {
        const view = currentView();
        if (!view) return;
        const { from, to } = view.state.selection;
        const active = markActive(view.state, editorSchema.marks.link);
        if (active) {
          view.dispatch(
            view.state.tr.removeMark(from, to, editorSchema.marks.link),
          );
          view.focus();
          return;
        }
        const href = window.prompt("链接地址", "https://")?.trim();
        if (!href) return;
        try {
          const url = new URL(href);
          if (!["http:", "https:", "mailto:"].includes(url.protocol)) return;
        } catch {
          return;
        }
        view.dispatch(
          view.state.tr.addMark(
            from,
            to,
            editorSchema.marks.link.create({ href, title: null }),
          ),
        );
        view.focus();
      },
    },
    commandButton("table", "插入 3×3 表格", IconTable, () =>
      insertTable(editorSchema),
    ),
    commandButton(
      "row-add",
      "在下方增加行",
      IconRowInsertBottom,
      () => addRowAfter,
    ),
    commandButton(
      "column-add",
      "在右侧增加列",
      IconColumnInsertRight,
      () => addColumnAfter,
    ),
    commandButton("row-remove", "删除当前行", IconRowRemove, () => deleteRow),
    commandButton(
      "column-remove",
      "删除当前列",
      IconColumnRemove,
      () => deleteColumn,
    ),
    commandButton("table-remove", "删除表格", IconTableOff, () => deleteTable),
    commandButton("image-smaller", "缩小图片", IconPhotoMinus, () =>
      resizeSelectedImage(editorSchema, -120),
    ),
    commandButton("image-larger", "放大图片", IconPhotoPlus, () =>
      resizeSelectedImage(editorSchema, 120),
    ),
    commandButton("image-remove", "删除图片", IconTrash, () =>
      removeSelectedImage(editorSchema),
    ),
    commandButton("undo", "撤销", IconArrowBackUp, () => undo),
    commandButton("redo", "重做", IconArrowForwardUp, () => redo),
    {
      key: "clear",
      label: "清除格式",
      icon: IconClearFormatting,
      active: () => false,
      enabled: (state) => !state.selection.empty,
      run: () => {
        const view = currentView();
        if (!view) return;
        const { from, to } = view.state.selection;
        view.dispatch(view.state.tr.removeMark(from, to));
        setBlockType(editorSchema.nodes.paragraph)(view.state, view.dispatch);
        view.focus();
      },
    },
  ];
}

function emailSchema(): Schema {
  const base = buildMessageSchema(
    ["strong", "em", "code", "link", "strike"],
    ["bulletList", "orderedList", "codeBlock", "blockquote", "image", "table"],
  );
  const underline = {
    parseDOM: [
      { tag: "u" },
      {
        style: "text-decoration",
        getAttrs: (value: string) =>
          value.toLocaleLowerCase().includes("underline") ? null : false,
      },
    ],
    toDOM: () => ["u", 0] as const,
  };
  const nodes: Record<string, NodeSpec> = {};
  base.spec.nodes.forEach((name, spec) => {
    const withUnderline =
      typeof spec.marks === "string" && spec.marks.length > 0
        ? { ...spec, marks: `${spec.marks} underline` }
        : spec;
    nodes[name] =
      name === "paragraph"
        ? {
            ...withUnderline,
            attrs: {
              ...(withUnderline.attrs ?? {}),
              signatureSpacer: { default: false },
            },
            parseDOM: [
              {
                tag: 'p[data-crm-signature-spacer="true"]',
                getAttrs: () => ({ signatureSpacer: true }),
              },
              { tag: "p" },
            ],
            toDOM: (node) => [
              "p",
              node.attrs.signatureSpacer
                ? { "data-crm-signature-spacer": "true" }
                : {},
              0,
            ],
          }
        : name === "image"
        ? {
            inline: true,
            group: "inline",
            draggable: true,
            attrs: {
              src: {},
              alt: { default: null },
              title: { default: null },
              width: { default: 480 },
            },
            parseDOM: [
              {
                tag: "img[src]",
                getAttrs: (node) => {
                  if (!(node instanceof HTMLImageElement)) return false;
                  const src = node.getAttribute("src") ?? "";
                  if (
                    !/^\/api\/v1\/case-workspace\/[0-9a-f-]{36}\/rich-text-images\/[0-9a-f-]{36}$/i.test(
                      src,
                    )
                  )
                    return false;
                  return {
                    src,
                    alt: node.getAttribute("alt"),
                    title: node.getAttribute("title"),
                    width: Math.min(
                      960,
                      Math.max(120, Number(node.getAttribute("width")) || 480),
                    ),
                  };
                },
              },
            ],
            toDOM: (node) => [
              "img",
              {
                src: node.attrs.src,
                alt: node.attrs.alt,
                title: node.attrs.title,
                width: node.attrs.width,
              },
            ],
          }
        : withUnderline;
  });
  const marks: Record<string, MarkSpec> = {};
  nodes.mention = {
    inline: true,
    group: "inline",
    atom: true,
    selectable: false,
    attrs: { userId: {}, label: {} },
    leafText: (node) => `@${node.attrs.label}`,
    parseDOM: [
      {
        tag: "span[data-mention-user-id]",
        getAttrs: (dom) => {
          const userId = dom.getAttribute("data-mention-user-id") ?? "";
          const text = dom.textContent ?? "";
          if (
            !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
              userId,
            ) ||
            !text.startsWith("@") ||
            !text.slice(1).trim()
          )
            return false;
          return { userId, label: text.slice(1) };
        },
      },
    ],
    toDOM: (node) => [
      "span",
      { "data-mention-user-id": node.attrs.userId },
      `@${node.attrs.label}`,
    ],
  };
  nodes.signature = {
    group: "block",
    content: "block+",
    isolating: true,
    parseDOM: [
      {
        tag: 'div[data-crm-signature="true"]',
      },
    ],
    toDOM: () => ["div", { "data-crm-signature": "true" }, 0],
  };
  base.spec.marks.forEach((name, spec) => {
    marks[name] = spec;
  });
  marks.underline = underline;
  return new Schema({ nodes, marks });
}

function createState(editorSchema: Schema, html: string): EditorState {
  return EditorState.create({
    schema: editorSchema,
    doc: documentFromHtml(editorSchema, html),
    plugins: [
      history(),
      keymap({ "Mod-z": undo, "Shift-Mod-z": redo, "Mod-y": redo }),
      keymap(baseKeymap),
      tableEditing(),
    ],
  });
}

function documentFromHtml(editorSchema: Schema, html: string): ProseMirrorNode {
  const holder = document.createElement("div");
  holder.innerHTML = html.trim() || "<p></p>";
  return ProseMirrorDOMParser.fromSchema(editorSchema).parse(holder);
}

function snapshotFromDocument(doc: ProseMirrorNode): RichTextSnapshot {
  const holder = document.createElement("div");
  holder.appendChild(
    DOMSerializer.fromSchema(schema).serializeFragment(doc.content),
  );
  return {
    html: holder.innerHTML,
    text: doc
      .textBetween(0, doc.content.size, "\n", (node) =>
        node.type.name === "mention" ? `@${node.attrs.label}` : "\n",
      )
      .trim(),
  };
}

function mentionAtCursor(
  state: EditorState,
): { from: number; to: number; query: string } | null {
  const { empty, $from } = state.selection;
  if (!empty || !$from.parent.isTextblock || $from.parent.type.spec.code)
    return null;
  const before = $from.parent.textBetween(0, $from.parentOffset, "", "\ufffc");
  const match = /(?:^|[\s（(，,：:；;。！!？?])@([^@\s]*)$/.exec(before);
  if (!match) return null;
  return {
    from: $from.pos - match[1].length - 1,
    to: $from.pos,
    query: match[1],
  };
}

function snapshotFromHtml(html: string): RichTextSnapshot {
  return snapshotFromDocument(documentFromHtml(schema, html));
}

function replaceDocument(
  editor: EditorView | null,
  html: string,
): RichTextSnapshot | null {
  if (!editor) return null;
  const next = createState(schema, html);
  editor.updateState(next);
  return snapshotFromDocument(next.doc);
}

function markActive(state: EditorState, mark: MarkType): boolean {
  const { empty, from, to, $from } = state.selection;
  if (empty) return Boolean(mark.isInSet(state.storedMarks ?? $from.marks()));
  return state.doc.rangeHasMark(from, to, mark);
}

function nodeActive(state: EditorState, node: NodeType): boolean {
  const { $from, to } = state.selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    if ($from.node(depth).type === node) return true;
  }
  let found = false;
  state.doc.nodesBetween($from.pos, to, (candidate) => {
    if (candidate.type === node) found = true;
    return !found;
  });
  return found;
}

function insertTable(editorSchema: Schema): Command {
  return (state, dispatch) => {
    const rowType = editorSchema.nodes.table_row;
    const cellType = editorSchema.nodes.table_cell;
    const tableType = editorSchema.nodes.table;
    if (!rowType || !cellType || !tableType || nodeActive(state, tableType))
      return false;
    const rows = Array.from({ length: 3 }, () =>
      rowType.create(
        null,
        Array.from({ length: 3 }, () => cellType.createAndFill()).filter(
          (cell): cell is ProseMirrorNode => Boolean(cell),
        ),
      ),
    );
    const table = tableType.create(null, rows);
    if (dispatch) dispatch(state.tr.replaceSelectionWith(table));
    return true;
  };
}

function resizeSelectedImage(editorSchema: Schema, delta: number): Command {
  return (state, dispatch) => {
    if (!(state.selection instanceof NodeSelection)) return false;
    const { node, from } = state.selection;
    if (node.type !== editorSchema.nodes.image) return false;
    if (dispatch) {
      const width = Math.min(
        960,
        Math.max(120, Number(node.attrs.width ?? 480) + delta),
      );
      dispatch(
        state.tr.setNodeMarkup(from, undefined, { ...node.attrs, width }),
      );
    }
    return true;
  };
}

function removeSelectedImage(editorSchema: Schema): Command {
  return (state, dispatch) => {
    if (
      !(state.selection instanceof NodeSelection) ||
      state.selection.node.type !== editorSchema.nodes.image
    )
      return false;
    if (dispatch) dispatch(state.tr.deleteSelection());
    return true;
  };
}
